import type React from "react";
import { getAttachedDate, getDetachedDay } from "@laborchart-modules/common/dist/datetime/date";
import type { WorkDays } from "@laborchart-modules/common/dist/postgres/schemas/common/custom-json";
import type { BryntumTaskBoard } from "@bryntum/taskboard-react-thin";
import type { CategoryTask } from "@laborchart-modules/lc-core-api/dist/api/assignments/load-boards";
import { BoardsTaskType } from "@laborchart-modules/lc-core-api/dist/api/assignments/load-boards";
import { ResourceType } from "@/react/prop-types";
import { ProcoreStore } from "@/stores/procore-store.core";
import type { GanttTearsheetRef } from "../gantt/gantt-tearsheets";
import { getLastWorkDate } from "@/lib/utils/date-2";
import { publish } from "./hooks/boards-event-bus";

interface ExternalDropHandlerParams {
   taskBoardRef: React.RefObject<BryntumTaskBoard>;
   createAssignmentTearsheetRef: React.RefObject<GanttTearsheetRef>;
}

type ResourceTag = {
   color: string;
   name: string;
   abbreviation: string;
   require_expr_date: boolean;
   expr_days_warning: number;
   expr_date?: string;
};

type ResourceDragItem = {
   resource_id: string;
   resource_title: string;
   position_name: string;
   resource_color: string;
   employee_number: string | null;
   resource_hourly_wage: number;
   job_title_rate: number;
   resource_tags: ResourceTag[];
   resource_address_1: string;
   resource_address_2: string | null;
   resource_city_town: string | null;
   resource_state_province: string | null;
   resource_zipcode: string | null;
   is_smart_bench?: boolean;
   tags_match_ratio?: number;
   job_title_match_status_score?: number;
   proximity?: number;
};

// Bryntum column record type - represents a column model with getData accessor
type ColumnRecord = {
   id: string | number;
   getData: (key: string) => string | number | null;
};

type RequestRecord = {
   id: string;
   project_id: string;
   start_day: number;
   end_day: number;
   start_time: string;
   end_time: string;
   work_days: WorkDays;
};

type TaskDates = {
   selectedDay: number;
   endDay: number;
   projectStartTime: string;
   projectEndTime: string;
   projectWorkDays: WorkDays;
};

// Global storage for drag data (since dataTransfer is not available in all events)
let touchDragData: any = null;
let globalDragData: any = null;
let isDragging = false;

// Helper function to check if dragging a single resource
const isSingleResource = (resourceData: any): boolean => {
   if (!resourceData?.data) return false;

   // If it's explicitly marked as multiple resources with array data
   if (resourceData.type === "resources" && Array.isArray(resourceData.data)) {
      return resourceData.data.length === 1;
   }

   // If it's a single resource object (not an array)
   return !Array.isArray(resourceData.data);
};
const buildDragAndDropProperties = (dragData: any) => {
   const singleResourceDragged = isSingleResource(dragData);
   const isSmartBench = singleResourceDragged ? dragData.data.is_smart_bench : false;
   return {
      scheduled_option: getScheduleChangesPreference(),
      assignment_count: singleResourceDragged ? 1 : dragData.data.length,
      request_count: 0,
      is_smart_bench: isSmartBench,
      job_title_match: isSmartBench
         ? dragData.data.job_title_match_status_score === 1
            ? true
            : false
         : null,
      tag_match_ratio: isSmartBench ? dragData.data.tags_match_ratio : null,
      proximity: isSmartBench ? dragData.data.proximity : null,
   };
};

export const DEFAULT_BOARDS_SCHEDULE_CHANGES = "choose_end_date";

const getScheduleChangesPreference = (): string =>
   localStorage.getItem("boards-schedule-changes") ?? DEFAULT_BOARDS_SCHEDULE_CHANGES;

export const setupExternalDropHandler = ({
   taskBoardRef,
   createAssignmentTearsheetRef,
}: ExternalDropHandlerParams) => {
   const instance = taskBoardRef.current?.instance;
   if (!instance) return;

   // Helper function to compute task dates, times, and work days from a column record
   const computeTaskDates = (columnRecord: ColumnRecord): TaskDates => {
      // Get the selected day from the taskboard instead of always using today
      const selectedDay =
         (instance.project as any)?.transport?.load?.params?.day_filter ||
         getDetachedDay(new Date());

      // Get project settings from column record
      const projectStartTime = columnRecord.getData("daily_start_time") as string;
      const projectEndTime = columnRecord.getData("daily_end_time") as string;
      const projectWorkDays: WorkDays = {
         0: false,
         1: true,
         2: true,
         3: true,
         4: true,
         5: true,
         6: false,
      };
      const selectedBoardsScheduleChanges = getScheduleChangesPreference();
      let endDay = selectedDay;
      // Use project end date only when explicitly selected
      if (selectedBoardsScheduleChanges === "project_end_date") {
         const estEndDate = columnRecord.getData("est_end_date") as number | undefined;

         let useProjectEndDate = false;
         if (estEndDate) {
            const detachedDay =
               estEndDate > 99999999 ? getDetachedDay(new Date(estEndDate)) : estEndDate;

            if (detachedDay >= selectedDay) {
               // If the project end date falls on a non-work day, walk back to the last work day

               const endDate = getAttachedDate(detachedDay);
               if (!projectWorkDays[endDate.getDay() as keyof typeof projectWorkDays]) {
                  const lastWorkDay = getLastWorkDate({
                     fromDate: endDate,
                     workDays: projectWorkDays,
                  });
                  endDay = lastWorkDay ? getDetachedDay(lastWorkDay) : detachedDay;
               } else {
                  endDay = detachedDay;
               }
               useProjectEndDate = true;
            }
         }

         if (!useProjectEndDate) {
            const dayOffset = 7 * (taskBoardRef.current?.instance as any).company.tbd_weeks;
            const selectedDate = getAttachedDate(selectedDay);
            // Use setDate to avoid timezone issues when adding days
            const targetDate = new Date(selectedDate);
            targetDate.setDate(targetDate.getDate() + dayOffset);
            endDay = getDetachedDay(targetDate);
         }
      }

      return { selectedDay, endDay, projectStartTime, projectEndTime, projectWorkDays };
   };

   // Helper function to build common resource properties from resource data
   const buildResourceProperties = (resource: ResourceDragItem) => ({
      resource_id: resource.resource_id,
      name: resource.resource_title,
      job_title_name: resource.position_name,
      job_title_color: resource.resource_color,
      employee_number: resource.employee_number,
      resource_hourly_wage: resource.resource_hourly_wage,
      job_title_rate: resource.job_title_rate,
      resource_tags: resource.resource_tags,
      resource_address_1: resource.resource_address_1,
      resource_address_2: resource.resource_address_2,
      resource_city_town: resource.resource_city_town,
      resource_state_province: resource.resource_state_province,
      resource_zipcode: resource.resource_zipcode,
   });

   // Helper function to create a base task with dates and resource properties
   const createBaseTask = (resource: ResourceDragItem, columnRecord: ColumnRecord) => {
      const { selectedDay, endDay, projectStartTime, projectEndTime, projectWorkDays } =
         computeTaskDates(columnRecord);

      return {
         ...buildResourceProperties(resource),
         project_id: columnRecord.id,
         start_day: selectedDay,
         end_day: endDay,
         start_time: projectStartTime,
         end_time: projectEndTime,
         work_days: projectWorkDays,
         type: BoardsTaskType.ASSIGNMENT,
      };
   };

   // Helper function to create a task from resource data
   const createTaskFromResource = (resource: ResourceDragItem, columnRecord: ColumnRecord) =>
      createBaseTask(resource, columnRecord);

   const createTaskFromRequest = (requestData: RequestRecord, resource: ResourceDragItem) => ({
      ...buildResourceProperties(resource),
      request_id: requestData.id,
      project_id: requestData.project_id,
      start_day: requestData.start_day,
      end_day: requestData.end_day,
      start_time: requestData.start_time,
      end_time: requestData.end_time,
      work_days: requestData.work_days,
      type: BoardsTaskType.ASSIGNMENT,
   });

   // Helper function to parse drag data
   const parseDragData = (dataTransfer: DataTransfer) => {
      try {
         const jsonData = dataTransfer.getData("application/json");
         const textData = dataTransfer.getData("text/plain");

         return jsonData ? JSON.parse(jsonData) : textData ? JSON.parse(textData) : null;
      } catch (error) {
         console.error("Error parsing drag data:", error);
         return null;
      }
   };

   // Helper function to find column element
   const findColumnElement = (element: Element | null): Element | null => {
      while (element) {
         if (element.classList.contains("b-task-board-column-body-inner")) {
            const columnBody = element.closest(".b-task-board-column");
            if (columnBody) {
               return columnBody;
            }
         }
         element = element.parentElement;
      }
      return null;
   };

   // Helper function to find request card element (only for single resource drops)
   const findRequestCardElement = (
      element: Element | null,
      resourceData?: any,
   ): { cardElement: Element; taskId: string } | null => {
      // Only allow request card drops for single resources
      if (resourceData && !isSingleResource(resourceData)) {
         return null;
      }

      while (element) {
         if (element.classList.contains("b-task-board-card")) {
            // Get the task ID from the card element
            const taskId = element.getAttribute("data-task");
            if (taskId) {
               // Check if this is a request card by looking for the request header class
               const requestHeader = element.querySelector(".task-card-header-request");
               if (requestHeader) {
                  return { cardElement: element, taskId };
               }
            }
         }
         element = element.parentElement;
      }
      return null;
   };

   // Helper function to find category card element when dropping resources into a category
   const findCategoryCardElement = (
      element: Element | null,
   ): { cardElement: Element; taskId: string; categoryName: string } | null => {
      while (element) {
         if (element.classList.contains("b-task-board-card")) {
            // Get the task ID from the card element
            const taskId = element.getAttribute("data-task");
            if (taskId) {
               // Check if this is a category card by checking the cls attribute
               const clsAttr = element.getAttribute("cls");
               if (clsAttr && clsAttr.includes("category-task-card")) {
                  // Get the category name from the task store
                  const categoryRecord = (instance.project as any).taskStore.getById(taskId);
                  const categoryName = categoryRecord?.name || "Category";
                  return { cardElement: element, taskId, categoryName };
               }
            }
         }
         element = element.parentElement;
      }
      return null;
   };

   // Helper function to create a task from resource data with category assignment
   const createTaskFromCategory = (
      categoryData: CategoryTask,
      resource: ResourceDragItem,
      columnRecord: ColumnRecord,
   ) => {
      // Determine category_id and subcategory_id based on whether this is a subcategory
      let categoryId: string | null = null;
      let subcategoryId: string | null = null;

      if (categoryData.parent_id) {
         // This is a subcategory - parent_id is the category, this id is the subcategory
         categoryId = categoryData.parent_id;
         subcategoryId = categoryData.id;
      } else {
         // This is a top-level category
         categoryId = categoryData.id;
         subcategoryId = null;
      }

      const depth = categoryData.parent_id ? 2 : 1;

      return {
         ...createBaseTask(resource, columnRecord),
         category_id: categoryId,
         subcategory_id: subcategoryId,
         depth,
      };
   };

   // Helper function to handle resource assignment (shared between drag and touch)
   const handleResourceAssignment = (resourceData: any, columnElement: Element) => {
      // Extract column ID from element ID using regex to handle any taskboard instance number.
      // Bryntum generates element IDs like "b-taskboard-N-column-default-{columnId}" where N is an
      // auto-incrementing instance number that changes each time the taskboard component is mounted,
      // and the columnId is the ID of the project represented by the column. The project ID is needed to
      // create the assignment for the project.
      // The regex pattern matches: b-taskboard-(\d+)-column-default-(.+) and captures the columnId.
      const elementId = columnElement.id;
      const match = elementId.match(/b-taskboard-\d+-column-default-(.+)/);
      const columnId = match?.[1];
      if (!columnId) {
         return;
      }

      const columnRecord = instance.columns.getById(columnId);
      if (!columnRecord) {
         return;
      }

      // Handle both single and multiple resources
      const resources =
         resourceData.type === "resources" && Array.isArray(resourceData.data)
            ? resourceData.data
            : [resourceData.data];

      // Check if we should open tearsheet for choosing end date
      const selectedBoardsScheduleChanges = getScheduleChangesPreference();
      if (selectedBoardsScheduleChanges === "choose_end_date") {
         // Suspend auto sync before opening tearsheet
         (instance.project as any).suspendAutoSync();

         // Always pass people array (even for single resource)
         const people = resources.map((resource: any) => ({
            id: resource.resource_id,
            label: resource.resource_title,
         }));

         const { selectedDay } = computeTaskDates(columnRecord);

         createAssignmentTearsheetRef.current?.handleOpenTearsheet({
            projectId: String(columnRecord.id),
            people,
            startDate: getAttachedDate(selectedDay),
            resourceType: ResourceType.LABOR,
            callback: (data: any) => {
               const resourceInfo = resources.find(
                  (resource: any) => resource.resource_id === data.newTask.resourceId,
               );
               if (!resourceInfo) {
                  return;
               }
               instance.addTask(columnRecord as any, undefined, {
                  id: data.newTask.id,
                  name: data.newTask.name,
                  project_id: data.newTask.projectId,
                  resource_id: data.newTask.resource_id,
                  start_day: getDetachedDay(data.newTask.startDate),
                  end_day: getDetachedDay(data.newTask.endDate),
                  start_time: data.newTask.startTime,
                  end_time: data.newTask.endTime,
                  instruction_text: data.newTask.instructionText,
                  work_scope_text: data.newTask.workScopeText,
                  work_days: data.newTask.workDays,
                  job_title_name: data.newTask.jobTitleName,
                  job_title_color: data.newTask.jobTitleColor,
                  employee_number: resourceInfo.employee_number,
                  resource_hourly_wage: resourceInfo.resource_hourly_wage,
                  job_title_rate: resourceInfo.job_title_rate,
                  resource_tags: resourceInfo.resource_tags,
                  resource_address_1: resourceInfo.resource_address_1,
                  resource_address_2: resourceInfo.resource_address_2,
                  resource_city_town: resourceInfo.resource_city_town,
                  resource_state_province: resourceInfo.resource_state_province,
                  resource_zipcode: resourceInfo.resource_zipcode,
                  type: BoardsTaskType.ASSIGNMENT,
               });

               // Remove the resources from the bench only when user confirms assignment
               const resourceIds = resources.map((resource: any) => resource.resource_id);
               const event = new CustomEvent("resourceAssigned", {
                  detail: {
                     resourceIds: resourceIds,
                     resourceId: resourceIds[0], // For backward compatibility
                  },
               });
               document.dispatchEvent(event);

               (instance.project as any).acceptChanges();
               (instance.project as any).resumeAutoSync();
               publish("column-tasks-changed", { projectId: String(columnRecord.id) });
            },
            onClose: () => {
               (instance.project as any).resumeAutoSync();
            },
         });
         return; // Early return to prevent default task creation
      } else {
         // Create tasks for all resources (for non-tearsheet scenarios)
         const newTasks = resources
            .map((data: any) => createTaskFromResource(data, columnRecord))
            .filter(Boolean);

         // Add all tasks to the board in one batch
         if (newTasks.length > 0) {
            // Batch add tasks
            newTasks.forEach((task: any) => {
               instance.addTask(columnRecord as any, undefined, task);
            });

            // Remove the resources from the bench by dispatching an event
            const resourceIds = resources.map((data: any) => data.resource_id).filter(Boolean);
            if (resourceIds.length > 0) {
               const event = new CustomEvent("resourceAssigned", {
                  detail: {
                     resourceIds: resourceIds,
                     resourceId: resourceIds[0], // For backward compatibility
                  },
               });
               document.dispatchEvent(event);
            }

            publish("column-tasks-changed", { projectId: String(columnRecord.id) });
         }
      }
   };

   // Helper function to handle request filling when dropping on a request card
   const handleRequestFilling = (
      resourceData: any,
      requestCard: { cardElement: Element; taskId: string },
   ) => {
      const { taskId, cardElement } = requestCard;

      // Safety check: only allow single resource drops on request cards
      if (!isSingleResource(resourceData)) {
         console.warn("Multiple resources cannot be dropped on request cards");
         return;
      }

      // Find the column that contains this task card
      const columnElement = cardElement.closest(".b-task-board-column");
      if (!columnElement) {
         return;
      }

      // Extract column ID from element ID using regex to handle any taskboard instance number.
      // Bryntum generates element IDs like "b-taskboard-N-column-default-{columnId}" where N is an
      // auto-incrementing instance number that changes each time the taskboard component is mounted,
      // and the columnId is the ID of the project represented by the column. The project ID is needed to
      // create the assignment for the project.
      // The regex pattern matches: b-taskboard-(\d+)-column-default-(.+) and captures the columnId.
      const elementId = columnElement.id;
      const match = elementId.match(/b-taskboard-\d+-column-default-(.+)/);
      const columnId = match?.[1];
      if (!columnId) {
         return;
      }

      const columnRecord = instance.columns.getById(columnId);
      if (!columnRecord) {
         return;
      }

      const requestRecord = (instance.project as any).taskStore.getById(taskId);
      if (!requestRecord) {
         return;
      }

      // Since we've already verified this is a single resource, pass it directly
      const newTask = createTaskFromRequest(requestRecord, resourceData.data);
      // Suspend auto sync before removing the request from the project
      (instance.project as any).suspendAutoSync();
      (instance.project as any).taskStore.remove(requestRecord);
      (instance.project as any).acceptChanges();

      // Resume auto sync after removing the request from the project
      (instance.project as any).resumeAutoSync();

      if (newTask) {
         instance.addTask(columnRecord as any, undefined, newTask);
      }

      // Remove the resource from the bench by dispatching an event
      const resourceId = resourceData.data.resource_id;
      if (resourceId) {
         const event = new CustomEvent("resourceAssigned", {
            detail: {
               resourceIds: [resourceId],
               resourceId: resourceId, // For backward compatibility
            },
         });
         document.dispatchEvent(event);
      }

      publish("column-tasks-changed", { projectId: String(columnRecord.id) });
   };

   // Helper function to handle category drop when dropping on a category card
   const handleCategoryDrop = (
      resourceData: any,
      categoryCard: { cardElement: Element; taskId: string; categoryName: string },
   ) => {
      const { taskId, cardElement } = categoryCard;

      // Safety check: only allow single resource drops on category cards
      if (!isSingleResource(resourceData)) {
         console.warn("Multiple resources cannot be dropped on category cards");
         return;
      }

      // Find the column that contains this category card
      const columnElement = cardElement.closest(".b-task-board-column");
      if (!columnElement) {
         return;
      }

      // Extract column ID from element ID using regex to handle any taskboard instance number.
      const elementId = columnElement.id;
      const match = elementId.match(/b-taskboard-\d+-column-default-(.+)/);
      const columnId = match?.[1];
      if (!columnId) {
         return;
      }

      const columnRecord = instance.columns.getById(columnId);
      if (!columnRecord) {
         return;
      }

      const categoryRecord = (instance.project as any).taskStore.getById(taskId);
      if (!categoryRecord) {
         return;
      }

      const categoryWeight = categoryRecord.weight ?? categoryRecord.data?.weight ?? 0;
      const newTask = createTaskFromCategory(categoryRecord, resourceData.data, columnRecord);
      if (newTask) {
         (newTask as any).weight = categoryWeight + 1;
         instance.addTask(columnRecord as any, undefined, newTask);

         const taskStore = (instance.project as any).taskStore;
         if (taskStore) {
            taskStore.sort({ field: "weight", ascending: true });
         }
      }

      // Remove the resource from the bench by dispatching an event
      const resourceId = resourceData.data.resource_id;
      if (resourceId) {
         const event = new CustomEvent("resourceAssigned", {
            detail: {
               resourceIds: [resourceId],
               resourceId: resourceId, // For backward compatibility
            },
         });
         document.dispatchEvent(event);
      }

      publish("column-tasks-changed", { projectId: String(columnRecord.id) });
   };

   // HTML5 drag and drop handlers
   const taskboardElement = instance.element;
   if (taskboardElement) {
      const removeDropZoneHighlight = () => {
         const highlightedElements = taskboardElement.querySelectorAll(
            ".lc-drop-zone-highlight, .lc-request-drop-zone-highlight, .lc-category-drop-zone-highlight",
         );
         highlightedElements.forEach((element) => {
            element.classList.remove(
               "lc-drop-zone-highlight",
               "lc-request-drop-zone-highlight",
               "lc-category-drop-zone-highlight",
            );
            element.removeAttribute("data-category-name");
         });
      };

      const handleDragOver = (e: DragEvent) => {
         e.preventDefault();
         if (e.dataTransfer) {
            e.dataTransfer.dropEffect = "copy";
         }
      };

      const handleDragEnter = (e: DragEvent) => {
         e.preventDefault();

         const dropTarget = e.target as Element;

         // Use global drag data since dataTransfer is often null in dragenter
         const resourceData = globalDragData;

         // Check if dropping on a request card first (only for single resources)
         const requestCard = findRequestCardElement(dropTarget, resourceData);
         if (requestCard) {
            removeDropZoneHighlight();

            // Add request card highlight
            requestCard.cardElement.classList.add("lc-request-drop-zone-highlight");
            return;
         }

         // Check if dropping on a category card (only for single resources)
         const categoryCard = findCategoryCardElement(dropTarget);
         if (categoryCard) {
            removeDropZoneHighlight();

            // Add category card highlight with category name
            categoryCard.cardElement.classList.add("lc-category-drop-zone-highlight");
            categoryCard.cardElement.setAttribute("data-category-name", categoryCard.categoryName);
            return;
         }

         // Fall back to column detection
         const columnElement = findColumnElement(dropTarget);
         if (columnElement) {
            removeDropZoneHighlight();

            // Add highlight to the current column
            columnElement.classList.add("lc-drop-zone-highlight");
         }
      };

      const handleDragLeave = (e: DragEvent) => {
         e.preventDefault();

         // Only remove highlight if we're actually leaving the element
         const currentTarget = e.currentTarget as Element;
         const relatedTarget = e.relatedTarget as Element;

         if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
            removeDropZoneHighlight();
         }
      };

      const handleDrop = (e: DragEvent) => {
         e.preventDefault();

         removeDropZoneHighlight();

         const dropTarget = e.target as Element;
         const dataTransfer = e.dataTransfer;
         if (!dataTransfer) {
            return;
         }

         const resourceData = parseDragData(dataTransfer);
         if (!resourceData?.data) {
            return;
         }

         // Check if dropping on a request card first (only for single resources)
         const requestCard = findRequestCardElement(dropTarget, resourceData);
         if (requestCard) {
            ProcoreStore.logAnalyticsEvent({
               key: "company.workforce_planning.boards.drag_and_drop_completed",
               properties: buildDragAndDropProperties(resourceData),
            });
            handleRequestFilling(resourceData, requestCard);
            return;
         }

         const categoryCard = findCategoryCardElement(dropTarget);
         if (categoryCard) {
            handleCategoryDrop(resourceData, categoryCard);
            return;
         }

         // Fall back to column handling
         const columnElement = findColumnElement(dropTarget);
         if (columnElement) {
            ProcoreStore.logAnalyticsEvent({
               key: "company.workforce_planning.boards.drag_and_drop_completed",
               properties: buildDragAndDropProperties(resourceData),
            });
            handleResourceAssignment(resourceData, columnElement);
            return;
         }
         // No valid drop target found
         ProcoreStore.logAnalyticsEvent({
            key: "company.workforce_planning.boards.drag_and_drop_cancelled",
            properties: {},
         });
      };

      // Touch event handlers for tablet support
      const handleTouchMove = (e: TouchEvent) => {
         if (!isDragging || !touchDragData) return;

         e.preventDefault();
         const touch = e.touches[0];
         const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);

         removeDropZoneHighlight();

         const requestCard = findRequestCardElement(elementBelow, touchDragData);
         if (requestCard) {
            requestCard.cardElement.classList.add("lc-request-drop-zone-highlight");
            return;
         }

         // Check for category card (only for single resources)
         const categoryCard = findCategoryCardElement(elementBelow);
         if (categoryCard) {
            categoryCard.cardElement.classList.add("lc-category-drop-zone-highlight");
            categoryCard.cardElement.setAttribute("data-category-name", categoryCard.categoryName);
            return;
         }

         // Fall back to column detection
         const columnElement = findColumnElement(elementBelow);
         if (columnElement) {
            columnElement.classList.add("lc-drop-zone-highlight");
         }
      };

      const handleTouchEnd = (e: TouchEvent) => {
         if (!isDragging || !touchDragData) return;

         e.preventDefault();
         isDragging = false;

         removeDropZoneHighlight();

         const touch = e.changedTouches[0];
         const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);

         if (touchDragData) {
            // Check for request card first (only for single resources)
            const requestCard = findRequestCardElement(elementBelow, touchDragData);
            if (requestCard) {
               ProcoreStore.logAnalyticsEvent({
                  key: "company.workforce_planning.boards.drag_and_drop_completed",
                  properties: buildDragAndDropProperties(touchDragData),
               });
               // Event: "company.workforce_planning.boards.drag_and_drop_completed"
               // Properties: buildDragAndDropProperties(touchDragData)
               handleRequestFilling(touchDragData, requestCard);
            } else {
               // Check for category card
               const categoryCard = findCategoryCardElement(elementBelow);
               if (categoryCard) {
                  ProcoreStore.logAnalyticsEvent({
                     key: "company.workforce_planning.boards.drag_and_drop_completed",
                     properties: buildDragAndDropProperties(touchDragData),
                  });
                  handleCategoryDrop(touchDragData, categoryCard);
               } else {
                  // Fall back to column handling
                  const columnElement = findColumnElement(elementBelow);
                  if (columnElement) {
                     ProcoreStore.logAnalyticsEvent({
                        key: "company.workforce_planning.boards.drag_and_drop_completed",
                        properties: buildDragAndDropProperties(touchDragData),
                     });
                     handleResourceAssignment(touchDragData, columnElement);
                  } else {
                     ProcoreStore.logAnalyticsEvent({
                        key: "company.workforce_planning.boards.drag_and_drop_cancelled",
                        properties: {},
                     });
                  }
               }
            }
         }

         // Clear touch drag data
         touchDragData = null;
      };

      // Add event listeners
      taskboardElement.addEventListener("dragover", handleDragOver);
      taskboardElement.addEventListener("dragenter", handleDragEnter);
      taskboardElement.addEventListener("dragleave", handleDragLeave);
      taskboardElement.addEventListener("drop", handleDrop);

      // Add touch event listeners for tablet support - use document for global capture during drag
      document.addEventListener("touchmove", handleTouchMove, { passive: false });
      document.addEventListener("touchend", handleTouchEnd, { passive: false });

      // Global drag state management for HTML5 drag and drop
      const handleGlobalDragStart = (e: DragEvent) => {
         if (e.dataTransfer) {
            const dragData = parseDragData(e.dataTransfer);
            if (dragData) {
               globalDragData = dragData;
            }
         }
      };

      const handleGlobalDragEnd = () => {
         globalDragData = null;
         removeDropZoneHighlight();
      };

      // Global touch drag state management
      const handleGlobalTouchStart = (e: CustomEvent) => {
         touchDragData = e.detail.dragData;
         isDragging = true;
      };

      const handleGlobalTouchCancel = () => {
         isDragging = false;
         touchDragData = null;
         removeDropZoneHighlight();
      };

      // Listen for global drag events to capture drag data
      document.addEventListener("dragstart", handleGlobalDragStart);
      document.addEventListener("dragend", handleGlobalDragEnd);

      // Listen for custom events from resource cards
      document.addEventListener("resourceTouchDragStart", handleGlobalTouchStart as EventListener);
      document.addEventListener(
         "resourceTouchDragCancel",
         handleGlobalTouchCancel as EventListener,
      );

      return () => {
         taskboardElement.removeEventListener("dragover", handleDragOver);
         taskboardElement.removeEventListener("dragenter", handleDragEnter);
         taskboardElement.removeEventListener("dragleave", handleDragLeave);
         taskboardElement.removeEventListener("drop", handleDrop);
         document.removeEventListener("touchmove", handleTouchMove);
         document.removeEventListener("touchend", handleTouchEnd);
         document.removeEventListener("dragstart", handleGlobalDragStart);
         document.removeEventListener("dragend", handleGlobalDragEnd);
         document.removeEventListener(
            "resourceTouchDragStart",
            handleGlobalTouchStart as EventListener,
         );
         document.removeEventListener(
            "resourceTouchDragCancel",
            handleGlobalTouchCancel as EventListener,
         );
      };
   }
};
